import { withChildren } from '@builder.io/react'
import { Cache } from 'aws-amplify'
import { growthbookQueueUtil } from 'components/util/Growthbook'
import React from 'react'

/**
 * A React component that allows for A/B testing of child components within Builder.io.
 *
 * This component randomly selects and renders one of its child components, evenly distributing the
 * selection across all child components. This is useful for A/B testing different variations of a
 * component or feature within the Builder.
 *
 * Use this when you want to avoid a component flashing between variations due to loading the default
 * then waiting for Growthbook to respond with the variant ID.
 *
 * Within Builder, all components are visible for easier setup.
 *
 * The component takes the following props:
 *
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The child components to be A/B tested.
 * @param {string} [props.growthbookFeatureId] - The Growthbook feature ID.
 * @param {string} [props.split] - The AB percentage split.
 * @param {boolean} [props.enabled] - Status switch.
 *
 * @returns {React.ReactElement} The randomly selected child component.
 */
const AbTest = (props: any): React.ReactElement => {
  const {
    growthbookFeatureId = '',
    enabled = false,
    split = ''
  }: { growthbookFeatureId: string; enabled: boolean; split: string } = props
  let { children = [] } = props

  const showVariant = (variantId: number) => {
    if (children[variantId]?.props?.block?.component?.options)
      children[variantId].props.block.component.options.visible = true
    return children[variantId]
  }

  const isInIframe = () => {
    if (typeof window === 'undefined') return false
    try {
      return window.self !== window.top
    } catch (_err) {
      return true
    }
  }
  if (isInIframe()) {
    // Detect if in the builder editor iframe and show all child components for easier editing

    if (children[0].props?.block?.component?.name !== 'AbSkeleton')
      return <div>Please add a AB Skeleton container as the first item...</div>

    for (const child of children) {
      if (!['AbSkeleton', 'AbContainer'].includes(child?.props?.block?.component?.name))
        return <div>AB test component should only contain 'AB Skeleton' and 'AB Container' components...</div>
      if (child?.props?.block?.component?.options) child.props.block.component.options.visible = true
    }

    if (!growthbookFeatureId) return <div>Please set Growthbook Feature ID...</div>

    if (children.length === 0) return <div>Please add child components for AB testing...</div>

    if (split) {
      const splitValues = split.split(',').map(Number)
      if (splitValues.length !== children.length - 1)
        return (
          <div>
            The number of split values must match the number of child components. The split value must be comma
            separated with no spaces e.g. '50,50' for two variants on a 50/50 split.
          </div>
        )

      const totalWeight = splitValues.reduce((sum, w) => sum + w, 0)
      if (totalWeight !== 100) return <div>The split values must total 100.</div>
    }

    return (
      <div>
        {!enabled && <div>Note: AB not enabled in Builder settings...</div>}
        <div>{children}</div>
      </div>
    )
  } else {
    // Show only skeleton on SSR, or filter it from variants
    if (typeof window === 'undefined') {
      children = children.filter(c => c.props?.block?.component?.name === 'AbSkeleton')
      if (!children.length) return <div></div>
      return showVariant(0)
    } else {
      children = children.filter(c => c.props?.block?.component?.name !== 'AbSkeleton')
    }

    // Deployed version, fallback to first item if disabled, or exit component if no children
    if ((!enabled || !growthbookFeatureId) && children.length) return showVariant(0)
    if (!children.length) return <div></div>
  }

  // Fallback for only a single child component, else don't return as we don't want an AB variant in the SSR html
  if (children.length === 1 && typeof window === 'undefined') return showVariant(0)
  if (typeof window === 'undefined') return <div></div>

  // Show the version they saw last time
  const key = `builderAbComponent_${growthbookFeatureId}`
  let variantId = Cache.getItem(key)
  if (typeof variantId === 'number') return showVariant(variantId)

  // Decide AB variant client side to avoid the delay of getting the data from Growthbook and the content flash
  if (split) {
    const weights = split.split(',').map(Number)
    const getRandomIndexWithWeights = weights => {
      const totalWeight = weights.reduce((sum, w) => sum + w, 0)
      let randomValue = Math.random() * totalWeight
      let index = 0
      while (randomValue > 0) {
        randomValue -= weights[index]
        index++
      }
      return index - 1
    }
    variantId = getRandomIndexWithWeights(weights)
  } else {
    variantId = Math.floor(Math.random() * children.length)
  }

  // Send decision to GA then cache it
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'viewed_experiment', {
      event_category: 'experiment',
      experiment_id: growthbookFeatureId,
      variation_id: variantId
    })
  }

  growthbookQueueUtil(growthbookFeatureId, variantId)

  Cache.setItem(key, variantId, { expires: Date.now() + 90 * 24 * 60 * 60 * 1000 }) // 90 days
  return showVariant(variantId)
}

const builderChildren = withChildren(AbTest)
export default builderChildren
